/**
 * apiService.js — Centralized API Service Layer for EduGate
 *
 * All functions use the axios instance configured in App.js
 * (baseURL = REACT_APP_BACKEND_URL/api, Authorization header automatic).
 *
 * Usage:
 *   import { studentsApi, teachersApi } from '../services/apiService';
 *   const students = await studentsApi.getAll();
 */

import axios from 'axios';

// ══════════════════════════════════════════════════════════
// Pagination helper
// ══════════════════════════════════════════════════════════
// [ISSUE-6/1 fix 2026-07] Every list endpoint is paginated server-side
// (students/teachers/parents default limit 50 max 200; fees/payments/grades/
// attendance/salaries/expenses default 200 max 1000). Screens that need the
// FULL dataset (finance 12-month grid, reports aggregation, dashboards) were
// calling them with no params and silently working on a truncated slice —
// numbers looked random once a school outgrew the default page size.
// fetchAllPages loops skip/limit until a short page, with a hard safety cap.
export const fetchAllPages = async (url, params = {}, pageSize = 1000, maxRows = 50000) => {
  const all = [];
  let skip = 0;
  for (;;) {
    const { data } = await axios.get(url, { params: { ...params, skip, limit: pageSize } });
    if (!Array.isArray(data)) return data; // defensive: non-list response
    all.push(...data);
    if (data.length < pageSize || all.length >= maxRows) break;
    skip += pageSize;
  }
  return all;
};

// ══════════════════════════════════════════════════════════
// Auth API
// ══════════════════════════════════════════════════════════
export const authApi = {
  login: (username, password, role) =>
    axios.post('/auth/login', { username, password, role }).then(r => r.data),

  logout: () =>
    axios.post('/auth/logout').then(r => r.data),

  refreshToken: () =>
    axios.post('/auth/refresh').then(r => r.data),

  getMe: () =>
    axios.get('/auth/me').then(r => r.data),

  updateProfile: (data) =>
    axios.put('/auth/profile', data).then(r => r.data),

  changePassword: (currentPassword, newPassword) =>
    axios.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }).then(r => r.data),

  requestPasswordReset: (username, role) =>
    axios.post('/auth/reset-request', { username, role }).then(r => r.data),

  resetPassword: (username, code, newPassword) =>
    axios.post('/auth/reset-password', { username, code, new_password: newPassword }).then(r => r.data),

  getResetRequests: () =>
    axios.get('/auth/reset-requests').then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Dashboard API
// ══════════════════════════════════════════════════════════
export const dashboardApi = {
  getStats: () =>
    axios.get('/dashboard/stats').then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Session / subscription API [PHASE-5 2026-10]
// ══════════════════════════════════════════════════════════
export const sessionApi = {
  // (The profile, session end and subscription_lock: authApi.getMe.)
  // school_admin only: status, end dates and the platform contact.
  getRenewalInfo: () =>
    axios.get('/subscription/renewal-info').then(r => r.data),

  // The platform contact for any signed-in user (from platform settings,
  // never hardcoded).
  getPlatformContact: () =>
    axios.get('/platform-settings').then(r => ({
      phone: (r.data && r.data.contact_phone) || '',
      email: (r.data && r.data.contact_email) || '',
    })),
};

// ══════════════════════════════════════════════════════════
// Students API
// ══════════════════════════════════════════════════════════
export const studentsApi = {
  // [ISSUE-6/1 fix 2026-07] Fetch ALL pages — server caps at 200/page and the
  // old single call silently returned only the first 50 students.
  getAll: (params = {}) =>
    fetchAllPages('/students', params, 200),

  getMe: () =>
    axios.get('/students/me').then(r => r.data),

  getById: (id) =>
    axios.get(`/students/${id}`).then(r => r.data),

  // [PHASE-4 2026-10] Everything the student drawer shows, for one year, in
  // ONE request. Sections the caller may not read come back null.
  getProfile: (id, academicYear) =>
    axios.get(`/students/${id}/profile`, { params: academicYear ? { academic_year: academicYear } : {} })
      .then(r => r.data),

  // GDPR Art. 9 — the dedicated, audit-logged read. Only the edit form uses it.
  getMedical: (id) =>
    axios.get(`/students/${id}/medical`).then(r => r.data),

  getByNumber: (studentNumber) =>
    axios.get(`/students/by-number/${studentNumber}`).then(r => r.data),

  create: (data) =>
    axios.post('/students', data).then(r => r.data),

  update: (id, data) =>
    axios.put(`/students/${id}`, data).then(r => r.data),

  delete: (id) =>
    axios.delete(`/students/${id}`).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Teachers API
// ══════════════════════════════════════════════════════════
export const teachersApi = {
  // [ISSUE-6/1 fix 2026-07] Fetch ALL pages (server default was 50).
  getAll: () =>
    fetchAllPages('/teachers', {}, 200),

  getMe: () =>
    axios.get('/teachers/me').then(r => r.data),

  getById: (id) =>
    axios.get(`/teachers/${id}`).then(r => r.data),

  // [PHASE-4 2026-10] Everything the teacher drawer shows, in ONE request.
  getProfile: (id, academicYear) =>
    axios.get(`/teachers/${id}/profile`, { params: academicYear ? { academic_year: academicYear } : {} })
      .then(r => r.data),

  create: (data) =>
    axios.post('/teachers', data).then(r => r.data),

  update: (id, data) =>
    axios.put(`/teachers/${id}`, data).then(r => r.data),

  delete: (id) =>
    axios.delete(`/teachers/${id}`).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Parents API
// ══════════════════════════════════════════════════════════
export const parentsApi = {
  // [ISSUE-6/1 fix 2026-07] Fetch ALL pages (server default was 50).
  getAll: () =>
    fetchAllPages('/parents', {}, 200),

  create: (data) =>
    axios.post('/parents', data).then(r => r.data),

  update: (id, data) =>
    axios.put(`/parents/${id}`, data).then(r => r.data),

  delete: (id) =>
    axios.delete(`/parents/${id}`).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Grades API
// ══════════════════════════════════════════════════════════
export const gradesApi = {
  // [ISSUE-6/1 fix 2026-07] Fetch ALL pages (server default was 200 — report
  // and rankings math silently ran on a truncated slice).
  getAll: (params = {}) =>
    fetchAllPages('/grades', params, 1000),

  getByStudent: (studentId, params = {}) =>
    fetchAllPages('/grades', { student_id: studentId, ...params }, 1000),

  create: (data) =>
    axios.post('/grades', data).then(r => r.data),

  update: (id, data) =>
    axios.put(`/grades/${id}`, data).then(r => r.data),

  delete: (id) =>
    axios.delete(`/grades/${id}`).then(r => r.data),

  // [BUG-3 2026-06] Class rankings by average across subjects. [PHASE-4
  // 2026-10] Admins, staff with view_reports, and a teacher for their classes.
  getRankings: (params = {}) =>
    axios.get('/grades/rankings', { params }).then(r => r.data),

  // [PHASE-4 2026-10] Every class of one academic year (no cap), plus the
  // school's scale and number of terms: { classes, scale, terms_per_year }.
  getRankingClasses: (params = {}) =>
    axios.get('/grades/rankings/classes', { params }).then(r => r.data),
};

// [PHASE-2 2026-10] The grade grid: one class + subject + term per request,
// and every changed cell saved in ONE request (CLAUDE.md #12).
export const gradebookApi = {
  get: (params) =>
    axios.get('/grades/gradebook', { params }).then(r => r.data),

  save: (payload) =>
    axios.post('/grades/bulk', payload).then(r => r.data),

  // One student's year result, computed by the server's gradebook rules
  // (the figures a transcript prints). Never average raw rows in the browser.
  summary: (studentId, params = {}) =>
    axios.get('/grades/summary', { params: { student_id: studentId, ...params } }).then(r => r.data),
};

// [PHASE-2 2026-10] Transcripts and certificates. `issue` gives each
// document a verification code; `preview` never does.
export const transcriptsApi = {
  classes: (params = {}) =>
    axios.get('/transcripts/classes', { params }).then(r => r.data),

  students: (className, params = {}) =>
    axios.get(`/transcripts/classes/${encodeURIComponent(className)}/students`, { params }).then(r => r.data),

  preview: (params) =>
    axios.get('/transcripts/preview', { params }).then(r => r.data),

  issue: (payload) =>
    axios.post('/transcripts/issue', payload).then(r => r.data),
};

// [PHASE-2 2026-10] No login. A 404 is an answer ("not found"), not an error.
export const publicApi = {
  // [AUDIT-2026-10-28] The platform contact from its settings (CLAUDE.md #22).
  contact: () =>
    axios.get('/public/contact').then(r => r.data || {}),

  verifyDocument: (code) =>
    axios.get(`/public/verify/${encodeURIComponent(code)}`, {
      validateStatus: (s) => s === 200 || s === 404,
    }).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Attendance API
// ══════════════════════════════════════════════════════════
export const attendanceApi = {
  // [ISSUE-1 fix 2026-07] Fetch ALL pages — with ~100 students one school
  // day exceeds the old 200-row single page, so attendance stats were being
  // computed from the OLDEST rows only (Mongo insertion order).
  getAll: (params = {}) =>
    fetchAllPages('/attendance', params),

  getByStudent: (studentId) =>
    fetchAllPages('/attendance', { student_id: studentId }),

  create: (data) =>
    axios.post('/attendance', data).then(r => r.data),

  createBulk: (records) =>
    axios.post('/attendance/bulk', records).then(r => r.data),

  update: (id, data) =>
    axios.put(`/attendance/${id}`, data).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Messages API
// [H-3 / C-2 / C-10 fix 2026-05-02] Paths now match the backend exactly:
//   - getInbox uses /messages/inbox (was /messages — no such route)
//   - markAllRead uses POST /messages/inbox/mark-read (no longer a GET side-effect)
//   - markRead uses PATCH /messages/{id}/read (was PUT — wrong verb)
// ══════════════════════════════════════════════════════════
export const messagesApi = {
  getInbox: () =>
    axios.get('/messages/inbox').then(r => r.data),

  // Backwards-compatible alias for callers still wired to getAll(); both
  // resolve to the inbox.
  getAll: () =>
    axios.get('/messages/inbox').then(r => r.data),

  getSent: () =>
    axios.get('/messages/sent').then(r => r.data),

  unreadCount: () =>
    axios.get('/messages/unread-count').then(r => r.data),

  send: (recipientId, message, subject = '', studentId = null) =>
    axios.post('/messages/send', {
      recipient_id: recipientId,
      message,
      subject,
      student_id: studentId,
    }).then(r => r.data),

  sendBulk: (targetType, className, content, subject) =>
    axios.post('/messages/bulk', {
      target_type: targetType,
      class_name: className,
      content,
      subject,
    }).then(r => r.data),

  sendToStudent: (studentId, content, subject = '', notifyParent = true) =>
    axios.post('/messages/send-to-student', {
      student_id: studentId,
      content,
      subject,
      notify_parent: notifyParent,
    }).then(r => r.data),

  markAllRead: () =>
    axios.post('/messages/inbox/mark-read').then(r => r.data),

  markRead: (messageId) =>
    axios.patch(`/messages/${messageId}/read`).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Notifications API
// [H-3 fix 2026-05-02] markRead uses PATCH (was PUT — wrong verb).
// ══════════════════════════════════════════════════════════
export const notificationsApi = {
  getAll: () =>
    axios.get('/notifications').then(r => r.data),

  markRead: (id) =>
    axios.patch(`/notifications/${id}/read`).then(r => r.data),

  markAllRead: () =>
    axios.patch('/notifications/read-all').then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Analytics API
// ══════════════════════════════════════════════════════════
export const analyticsApi = {
  // [AUDIT-2026-10 perf] Every reports-screen figure, aggregated server-side
  // for one period: { period: 'monthly'|'annual', year, month }.
  getReportSummary: (params) =>
    axios.get('/reports/summary', { params }).then(r => r.data),

  // [PHASE-4 2026-10] { academic_year, method, rankings } for one academic
  // year ({ academic_year } param, default the active one). Each row carries
  // its server-computed competition rank.
  getRankings: (params = {}) =>
    axios.get('/analytics/rankings', { params }).then(r => r.data),

  getStudentAnalytics: (studentId) =>
    axios.get(`/analytics/student/${studentId}`).then(r => r.data),

  // [PHASE-4 2026-10] { academic_year, method, classes }.
  getClassPerformance: (params = {}) =>
    axios.get('/analytics/class-performance', { params }).then(r => r.data),

  // [FEAT-2026-09] Early warning — students who need follow-up now, worst
  // first, each with the concrete facts behind the flag. The unpaid-fees
  // signal is only ever returned to a caller who may already read fees.
  getAtRisk: (params = {}) =>
    axios.get('/analytics/at-risk', { params }).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Finance API
// ══════════════════════════════════════════════════════════
export const financeApi = {
  // [ISSUE-6 fix 2026-07] All finance list reads fetch every page — the old
  // single call was capped at 200 rows server-side, so the 12-month grid
  // showed months as empty once a school outgrew the cap.
  // [AUDIT-2026-10 perf] Pass the academic year the screen shows: every year's
  // fees used to be paged in (8 requests / 2.4 MB on a 600-student school).
  getFees: (studentId = null, academicYear = null) =>
    fetchAllPages('/fees', {
      ...(studentId ? { student_id: studentId } : {}),
      ...(academicYear ? { academic_year: academicYear } : {}),
    }),

  createFee: (data) =>
    axios.post('/fees', data).then(r => r.data),

  updateFee: (id, data) =>
    axios.put(`/fees/${id}`, data).then(r => r.data),

  deleteFee: (id) =>
    axios.delete(`/fees/${id}`).then(r => r.data),

  // [PHASE-1b 2026-10] Every finance list answers for ONE academic year;
  // without academicYear the server uses the active year.
  getPayments: (studentId = null, academicYear = null) =>
    fetchAllPages('/payments', {
      ...(studentId ? { student_id: studentId } : {}),
      ...(academicYear ? { academic_year: academicYear } : {}),
    }),

  createPayment: (data) =>
    axios.post('/payments', data).then(r => r.data),

  getSalaries: (academicYear = null) =>
    fetchAllPages('/salaries', academicYear ? { academic_year: academicYear } : {}),

  createSalary: (data) =>
    axios.post('/salaries', data).then(r => r.data),

  getExpenses: (academicYear = null) =>
    fetchAllPages('/expenses', academicYear ? { academic_year: academicYear } : {}),

  createExpense: (data) =>
    axios.post('/expenses', data).then(r => r.data),

  // [PHASE-3 2026-10] Totals for one academic year, computed by the server.
  getStats: (academicYear = null) =>
    axios.get('/financial/stats', { params: academicYear ? { academic_year: academicYear } : {} })
      .then(r => r.data),

  // Bulk payments: the active year's unpaid fees, then ONE request for the
  // whole selection (dry_run: true previews it without writing).
  unpaidFees: (params = {}) =>
    axios.get('/fees/unpaid', { params: { limit: 2000, ...params } }).then(r => r.data),

  bulkPay: (body) =>
    axios.post('/payments/bulk', body).then(r => r.data),

  // One fee for every student of a class (dry_run: true previews it).
  createClassFees: (body) =>
    axios.post('/fees/class-batch', body).then(r => r.data),

  // The batch endpoints send the school header once; each document gets it.
  paymentReceipts: (ids) =>
    axios.post('/payments/receipts', { ids }).then(r => r.data.receipts.map(d => ({ ...r.data.school, ...d }))),

  feeReceipt: (feeId) =>
    axios.get(`/fees/${feeId}/receipt`).then(r => r.data),

  payrollPreview: (month) =>
    axios.get('/salaries/payroll', { params: { month } }).then(r => r.data),

  runPayroll: (body) =>
    axios.post('/salaries/payroll', body).then(r => r.data),

  payslips: (ids) =>
    axios.post('/salaries/payslips', { ids }).then(r => r.data.payslips.map(d => ({ ...r.data.school, ...d }))),

  salaryPayslip: (salaryId) =>
    axios.get(`/salaries/${salaryId}/payslip`).then(r => r.data),

  // [AUDIT-2026-10-16 L-10] The caller's own salary rows (teacher or staff).
  mySalaries: (academicYear = null) =>
    axios.get('/salaries/me', { params: academicYear ? { academic_year: academicYear } : {} })
      .then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Schools API
// ══════════════════════════════════════════════════════════
export const schoolsApi = {
  getAll: () =>
    axios.get('/schools').then(r => r.data),

  getById: (id) =>
    axios.get(`/schools/${id}`).then(r => r.data),

  getTrialStatus: (id) =>
    axios.get(`/schools/${id}/trial-status`).then(r => r.data),

  create: (data) =>
    axios.post('/schools', data).then(r => r.data),

  register: (data) =>
    axios.post('/schools/register', data).then(r => r.data),

  update: (id, data) =>
    axios.put(`/schools/${id}`, data).then(r => r.data),

  // [CLASSES-PORTAL 2026-10] 409 SUBJECT_EXISTS when it is already listed.
  addCustomSubject: (id, subject) =>
    axios.post(`/schools/${id}/custom-subjects`, { subject }).then(r => r.data),

  // [PHASE-1b 2026-10] 409 SUBJECT_IN_USE while a teacher (or, since stage 2,
  // a class) still has it.
  removeCustomSubject: (id, subject) =>
    axios.delete(`/schools/${id}/custom-subjects`, { params: { subject } }).then(r => r.data),

  delete: (id) =>
    axios.delete(`/schools/${id}`).then(r => r.data),

  updateSubscription: (id, data) =>
    axios.patch(`/admin/subscriptions/${id}`, data).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Search API
// ══════════════════════════════════════════════════════════
export const searchApi = {
  universal: (query) =>
    axios.get('/search', { params: { q: query } }).then(r => r.data),

  students: (query) =>
    axios.get('/search/student', { params: { q: query } }).then(r => r.data),

  teachers: (query) =>
    axios.get('/search/teacher', { params: { q: query } }).then(r => r.data),

  schools: (query) =>
    axios.get('/search/school', { params: { q: query } }).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Calendar API
// ══════════════════════════════════════════════════════════
export const calendarApi = {
  getEvents: (year, month) =>
    axios.get('/calendar/events', { params: { year, month } }).then(r => r.data),

  createEvent: (data) =>
    axios.post('/calendar/events', data).then(r => r.data),

  deleteEvent: (id) =>
    axios.delete(`/calendar/events/${id}`).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Constants API
// ══════════════════════════════════════════════════════════
export const constantsApi = {
  getAll: () =>
    axios.get('/constants').then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Academic Years API
// ══════════════════════════════════════════════════════════
export const academicYearsApi = {
  getAll: () =>
    axios.get('/academic-years').then(r => r.data),

  create: (data) =>
    axios.post('/academic-years', data).then(r => r.data),

  activate: (id) =>
    axios.patch(`/academic-years/${id}/activate`).then(r => r.data),

  rollover: (data) =>
    axios.post('/academic-year/rollover', data).then(r => r.data),

  updateStatus: (id, status) =>
    axios.patch(`/academic-years/${id}/status`, { status }).then(r => r.data),

  updateCalendar: (id, phases) =>
    axios.patch(`/academic-years/${id}/calendar`, { phases }).then(r => r.data),

  rollbackManual: (backup_id) =>
    axios.post('/academic-year/rollback-manual', { backup_id }).then(r => r.data),

  listBackups: () =>
    axios.get('/academic-year/backups').then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Teacher Documents API  [FEAT teacher-docs 2026-09]
// ══════════════════════════════════════════════════════════
// Personal identity documents. Every call is access-checked server-side;
// there is no public URL for a stored file, and downloads go through the
// authenticated endpoint below as a blob — so a link copied out of the app
// is worthless to whoever receives it.
export const teacherDocumentsApi = {
  getTypes: () =>
    axios.get('/teacher-documents/types').then(r => r.data),

  // Replace-semantics for the LIST only: a type removed here is deactivated
  // server-side, never deleted, so documents already uploaded under it keep
  // a meaningful label.
  saveTypes: (types) =>
    axios.put('/teacher-documents/types', { types }).then(r => r.data),

  list: (teacherId) =>
    axios.get(`/teachers/${teacherId}/documents`).then(r => r.data),

  upload: (teacherId, file, documentTypeId, note = '', onProgress) => {
    const form = new FormData();
    form.append('file', file);
    form.append('document_type_id', documentTypeId);
    form.append('note', note);
    return axios.post(`/teachers/${teacherId}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    }).then(r => r.data);
  },

  // Fetched as a blob through the authenticated endpoint rather than linked
  // to directly: the browser must send the Authorization header, and the
  // object URL it produces dies with the tab.
  download: (teacherId, documentId) =>
    axios.get(`/teachers/${teacherId}/documents/${documentId}/download`, {
      responseType: 'blob',
    }).then(r => r.data),

  remove: (teacherId, documentId) =>
    axios.delete(`/teachers/${teacherId}/documents/${documentId}`).then(r => r.data),

  storage: () =>
    axios.get('/teacher-documents/storage').then(r => r.data),

  // super_admin only — volume capacity, no document metadata.
  storageHealth: () =>
    axios.get('/admin/storage-health').then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Class Schedules API
// ══════════════════════════════════════════════════════════
export const schedulesApi = {
  getAll: (params = {}) =>
    axios.get('/schedules', { params }).then(r => r.data),

  getMyClass: () =>
    axios.get('/schedules/my-class').then(r => r.data),

  getByTeacher: (teacherId, params = {}) =>
    axios.get(`/schedules/teacher/${teacherId}`, { params }).then(r => r.data),

  // [PHASE-4 2026-10] School Directory: timetable rows matching the filters
  // (q, subject, teacher_id, class_name, day, period, academic_year) and each
  // (class, subject)'s periods per week, in one aggregation.
  directory: (params = {}) =>
    axios.get('/school-directory', { params }).then(r => r.data),
  // [CLASSES-PORTAL 2026-10] Who can cover a lesson (day + period, or day +
  // absent_teacher_id), and each teacher's weekly load.
  directoryCover: (params = {}) =>
    axios.get('/school-directory/cover', { params }).then(r => r.data),
  teacherLoad: (params = {}) =>
    axios.get('/school-directory/teacher-load', { params }).then(r => r.data),

  // [CLASSES-PORTAL 2026-10] The classes portal and the subjects page: every
  // class and subject of the year (roster, timetable, teachers, curriculum)
  // in one read; one class's card (students, timetable, bell, days) or one
  // subject's slots on demand.
  classPortal: (params = {}) =>
    axios.get('/class-portal', { params }).then(r => r.data),
  classCard: (className, params = {}) =>
    axios.get('/class-portal/class', { params: { ...params, class_name: className } }).then(r => r.data),
  subjectCard: (subject, params = {}) =>
    axios.get('/class-portal/subject', { params: { ...params, subject } }).then(r => r.data),
  // Stage 2: a class record (name from the list or free, grade level,
  // boys/girls label) and who teaches which subject in a class.
  createClass: (data) =>
    axios.post('/classes', data).then(r => r.data),
  updateClass: (id, data) =>
    axios.patch(`/classes/${id}`, data).then(r => r.data),
  deleteClass: (id) =>
    axios.delete(`/classes/${id}`).then(r => r.data),
  // The active year's rows follow the new name; archived years keep the old one.
  renameClass: (oldName, newName) =>
    axios.post('/classes/rename', { old_name: oldName, new_name: newName }).then(r => r.data),
  assignClassSubject: (data) =>
    axios.put('/class-subjects', data).then(r => r.data),
  removeClassSubject: (className, subject) =>
    axios.delete('/class-subjects', { params: { class_name: className, subject } }).then(r => r.data),

  create: (data) =>
    axios.post('/schedules', data).then(r => r.data),

  update: (id, data) =>
    axios.put(`/schedules/${id}`, data).then(r => r.data),

  remove: (id) =>
    axios.delete(`/schedules/${id}`).then(r => r.data),

  // [FEAT fast-builder 2026-09] Replace a class's COMPLETE week in ONE
  // request. The per-period POST above needs one call per (class, day), so a
  // 20-class build was 100 writes — past nginx's burst of 30, which is how
  // the tail of a build silently 429'd. Entries name a period by
  // `period_order` against the school's bell schedule; times are never sent.
  // Replace semantics: any period NOT in `entries` is removed.
  // [AUDIT-2026-10-16 S-DUP] (class, day) cells held by more than one
  // document, and keeping one version of one (keep_id) or merging them.
  duplicates: () =>
    axios.get('/schedules/duplicates').then(r => r.data),

  resolveDuplicate: ({ class_name, day, keep_id, merge }) =>
    axios.post('/schedules/duplicates/resolve', {
      class_name, day, ...(merge ? { merge: true } : { keep_id }),
    }).then(r => r.data),

  saveClassBulk: ({ class_name, entries, academic_year }) =>
    axios.put('/schedules/bulk', {
      class_name,
      entries,
      ...(academic_year ? { academic_year } : {}),
    }).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Exam / Test Schedule API  [FEAT exam-schedule 2026-07]
// ══════════════════════════════════════════════════════════
export const examSchedulesApi = {
  // academicYear/className are optional filters; omit for the active-year view.
  getAll: (params = {}) =>
    axios.get('/exam-schedules', { params }).then(r => r.data),

  create: (data) =>
    axios.post('/exam-schedules', data).then(r => r.data),

  update: (id, data) =>
    axios.put(`/exam-schedules/${id}`, data).then(r => r.data),

  remove: (id) =>
    axios.delete(`/exam-schedules/${id}`).then(r => r.data),
};

// ══════════════════════════════════════════════════════════
// Student PIN login  [PHASE-5 2026-10]
// ══════════════════════════════════════════════════════════
// Primary/middle pupils sign in with school code + student number + a
// 4-digit PIN. A PIN appears ONLY in the generate/reset responses; keep it in
// component memory for printing and never store it anywhere.
export const studentPinsApi = {
  // Same response body as authApi.login: { access_token, token_type, user, expires_in }.
  login: ({ school_number, student_number, pin }) =>
    axios.post('/auth/login/pin', { school_number, student_number, pin }).then(r => r.data),

  // { students: [{student_id, student_number, full_name, class_name, grade,
  //   uses_pin, has_pin, set_at, locked_until}], total, classes,
  //   pin_login_grades, school_number }
  list: (params = {}) =>
    axios.get('/student-pins', { params }).then(r => r.data),

  // One class OR up to 100 student ids. overwrite=false keeps existing PINs.
  generate: ({ class_name, student_ids, overwrite = false }) =>
    axios.post('/student-pins/generate', {
      ...(class_name ? { class_name } : {}),
      ...(student_ids ? { student_ids } : {}),
      overwrite,
    }).then(r => r.data),

  reset: (studentId) =>
    axios.post(`/student-pins/${studentId}/reset`).then(r => r.data),

  unlock: (studentId) =>
    axios.post(`/student-pins/${studentId}/unlock`).then(r => r.data),

  // Which grades use PIN login. Its OWN endpoint: the general school
  // settings save (schoolsApi.update) can neither change nor wipe it.
  getSettings: (schoolId) =>
    axios.get(`/schools/${schoolId}/pin-settings`).then(r => r.data),

  saveSettings: (schoolId, pinLoginGrades) =>
    axios.put(`/schools/${schoolId}/pin-settings`, { pin_login_grades: pinLoginGrades }).then(r => r.data),
};

// Printed timetables: the school header (name, logo, from /auth/me, which
// every role may read) and the bell (lessons + breaks, from /constants), in
// two parallel reads. The timetable rows themselves come from the screen.
export const timetablePrintApi = {
  context: () => Promise.all([authApi.getMe(), constantsApi.getAll()])
    .then(([me, constants]) => ({ me, constants })),
};

// ══════════════════════════════════════════════════════════
// Ratings API  [PHASE-6 2026-10]
// ══════════════════════════════════════════════════════════
// Every figure (averages, counts) comes from getSummary, computed on the
// server for one academic year; list() is history rows only, newest first.
const ratingsParams = (params = {}) => {
  const out = { ...params };
  if (!out.academic_year) delete out.academic_year;
  return out;
};

export const ratingsApi = {
  // { target_id?, target_type?, academic_year?, limit? (server max 1000) }
  list: (params = {}) =>
    axios.get('/ratings', { params: ratingsParams(params) }).then(r => r.data),

  // { target_id, target_type: 'teacher'|'student', rating: 1..RATING_SCALE,
  //   category: a key from lib/ratingCategories, notes? }
  create: (data) =>
    axios.post('/ratings', data).then(r => r.data),

  // School evaluations of teachers for one year (manage_teachers only; 403
  // otherwise): { academic_year, scale, categories, teachers, totals, method }
  getSummary: (academicYear) =>
    axios.get('/ratings/summary', { params: ratingsParams({ academic_year: academicYear }) }).then(r => r.data),
};

// [SUPPORT 2026-11] Support tickets. School staff write to the super admin;
// who they are is read from the session on the server, never sent from here.
export const supportApi = {
  // { category, urgency, subject, message, context?: { page, app_version } }
  createTicket: (data) =>
    axios.post('/support/tickets', data).then(r => r.data),
  // The sender's own tickets with every reply: { tickets }
  myTickets: () =>
    axios.get('/support/tickets/mine').then(r => r.data),
  // A follow-up (sender) or a reply (super admin): { message, status }
  addMessage: (ticketId, message) =>
    axios.post(`/support/tickets/${ticketId}/messages`, { message }).then(r => r.data),
  // Super admin: { tickets, counts } with { status?, urgency?, q? }
  list: (params = {}) =>
    axios.get('/support/tickets', { params }).then(r => r.data),
  // The whole ticket; for the super admin it also carries `live`.
  get: (ticketId) =>
    axios.get(`/support/tickets/${ticketId}`).then(r => r.data),
  setStatus: (ticketId, status) =>
    axios.patch(`/support/tickets/${ticketId}`, { status }).then(r => r.data),
};
